import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Cookie, ShieldCheck, BarChart3, Megaphone, X, ChevronDown } from "lucide-react";

const STORAGE_KEY = "sh_cookie_prefs_v1";
const OPEN_EVENT = "sh:open-cookie-prefs";

function readStoredPrefs() {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      necessary: true,
      analytics: !!parsed.analytics,
      marketing: !!parsed.marketing,
    };
  } catch (e) {
    return null;
  }
}

/**
 * Cookie consent banner — categories: necessary, analytics, marketing.
 * Necessary is always on. Choices persist in localStorage.
 * Other code can read window.__shCookiePrefs to gate scripts (e.g., analytics).
 */
export default function CookieConsent() {
  const stored = typeof window !== "undefined" ? readStoredPrefs() : null;
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState(false);
  const [prefs, setPrefs] = useState(
    stored || { necessary: true, analytics: false, marketing: false }
  );

  const persist = useCallback((next) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          ...next,
          necessary: true,
          decidedAt: new Date().toISOString(),
          version: 1,
        })
      );
    } catch (e) {
      /* ignore */
    }
    if (typeof window !== "undefined") {
      window.__shCookiePrefs = { ...next, necessary: true };
      window.dispatchEvent(
        new CustomEvent("sh:cookie-prefs-changed", { detail: window.__shCookiePrefs })
      );
    }
  }, []);

  useEffect(() => {
    const existing = readStoredPrefs();
    if (existing) {
      window.__shCookiePrefs = existing;
      return;
    }
    const t = setTimeout(() => setOpen(true), 900);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const handler = () => {
      setOpen(true);
      setDetails(true);
    };
    window.addEventListener(OPEN_EVENT, handler);
    return () => window.removeEventListener(OPEN_EVENT, handler);
  }, []);

  const acceptAll = () => {
    const next = { necessary: true, analytics: true, marketing: true };
    setPrefs(next);
    persist(next);
    setOpen(false);
  };

  const rejectNonEssential = () => {
    const next = { necessary: true, analytics: false, marketing: false };
    setPrefs(next);
    persist(next);
    setOpen(false);
  };

  const savePrefs = () => {
    persist(prefs);
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      data-testid="cookie-consent"
      role="dialog"
      aria-label="Cookie preferences"
      className="fixed inset-x-0 bottom-0 z-[60] px-3 sm:px-5 pb-3 sm:pb-5 pointer-events-none"
    >
      <div className="max-w-[1100px] mx-auto pointer-events-auto">
        <div className="relative rounded-2xl bg-white border border-line shadow-[0_30px_60px_-20px_rgba(15,42,71,0.28),0_8px_20px_-8px_rgba(15,42,71,0.12)] overflow-hidden">
          {/* Top accent */}
          <div className="h-[3px] w-full bg-gradient-to-r from-blue via-blue-dark to-green" />

          <div className="p-5 sm:p-6">
            <div className="flex items-start gap-4">
              <span className="hidden sm:inline-flex w-10 h-10 items-center justify-center rounded-xl bg-green-soft text-green shrink-0">
                <Cookie size={18} strokeWidth={1.7} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-display text-[15px] font-semibold text-ink tracking-tight">
                      We use cookies to keep SajiloHealth working well.
                    </div>
                    <p className="mt-1.5 text-[13px] text-textsoft leading-[1.55] max-w-2xl">
                      Necessary cookies keep the site secure and functional. With your consent, we
                      may also use analytics and marketing cookies to understand usage and improve
                      the platform. You can change your choice anytime. See our{" "}
                      <Link
                        to="/privacy"
                        className="font-semibold text-ink underline underline-offset-2 hover:text-green"
                      >
                        Privacy Policy
                      </Link>
                      .
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={rejectNonEssential}
                    data-testid="cookie-close"
                    aria-label="Close"
                    className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full text-textsoft hover:bg-paper-sunken transition-colors"
                  >
                    <X size={15} strokeWidth={2} />
                  </button>
                </div>

                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => setDetails((v) => !v)}
                    data-testid="cookie-manage-toggle"
                    className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-ink hover:text-green"
                  >
                    <span>{details ? "Hide details" : "Manage preferences"}</span>
                    <ChevronDown
                      size={13}
                      strokeWidth={2}
                      className={`transition-transform ${details ? "rotate-180" : ""}`}
                    />
                  </button>
                </div>

                {details && (
                  <div data-testid="cookie-categories" className="mt-4 grid sm:grid-cols-3 gap-2">
                    <CategoryRow
                      icon={ShieldCheck}
                      title="Necessary"
                      desc="Required for login, security, and core features."
                      locked
                      checked
                    />
                    <CategoryRow
                      testId="cookie-toggle-analytics"
                      icon={BarChart3}
                      title="Analytics"
                      desc="Helps us understand performance and improve product."
                      checked={prefs.analytics}
                      onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                    />
                    <CategoryRow
                      testId="cookie-toggle-marketing"
                      icon={Megaphone}
                      title="Marketing"
                      desc="Used to measure outreach and beta campaigns."
                      checked={prefs.marketing}
                      onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                    />
                  </div>
                )}

                <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2">
                  <button
                    type="button"
                    onClick={rejectNonEssential}
                    data-testid="cookie-reject"
                    className="inline-flex items-center justify-center px-4 py-2.5 rounded-full text-[13px] font-semibold text-textsoft hover:text-ink hover:bg-paper-sunken transition-colors"
                  >
                    Reject non-essential
                  </button>
                  {details ? (
                    <button
                      type="button"
                      onClick={savePrefs}
                      data-testid="cookie-save"
                      className="inline-flex items-center justify-center px-4 py-2.5 rounded-full text-[13px] font-semibold border border-line text-ink hover:bg-paper-sunken transition-colors"
                    >
                      Save preferences
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={acceptAll}
                    data-testid="cookie-accept-all"
                    className="btn btn-primary text-[13px]"
                  >
                    Accept all
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CategoryRow({ icon: Icon, title, desc, checked, locked, onChange, testId }) {
  return (
    <div className="rounded-xl border border-line bg-paper p-3.5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <span className="inline-flex w-7 h-7 items-center justify-center rounded-lg bg-white border border-line text-ink">
            <Icon size={13} strokeWidth={1.7} />
          </span>
          <div>
            <div className="text-[12.5px] font-semibold text-ink tracking-tight">
              {title}
              {locked && (
                <span className="ml-1.5 inline-block text-[9px] font-semibold tracking-[0.16em] uppercase text-green">
                  Always on
                </span>
              )}
            </div>
            <p className="mt-0.5 text-[11.5px] text-textsoft leading-[1.5]">{desc}</p>
          </div>
        </div>
        <ToggleSwitch checked={!!checked} locked={locked} onChange={onChange} testId={testId} />
      </div>
    </div>
  );
}

function ToggleSwitch({ checked, locked, onChange, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      data-testid={testId}
      onClick={() => !locked && onChange?.(!checked)}
      disabled={locked}
      className={`relative shrink-0 inline-flex h-5 w-9 items-center rounded-full transition-colors ${
        checked ? "bg-green" : "bg-line-strong"
      } ${locked ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-[18px]" : "translate-x-[3px]"
        }`}
      />
    </button>
  );
}

// Trigger helper used by Footer
export function openCookiePreferences() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(OPEN_EVENT));
  }
}
